import React, { useEffect, useState } from 'react';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import SalovareDatePicker from '../common/SalovareDatePicker';
import SwitchField from '../common/SwitchField';
import RecurrenceFields from './RecurrenceFields';
import { fieldLabelSx, nativeInputSx } from '../common/formFieldStyles';
import { EXPENSE_CATEGORIES } from '../../constants/Expenses';
import { IN_STORE_PAYMENT_METHODS, getStorePaymentMethodKey } from '../../constants/StorePaymentMethod';
import { validateExpenseForm } from '../../utils/expenseValidation';
import { parseCalendarDateKey, toCalendarDateKey } from '../../utils/dateFormatUtils';

const buildInitial = (expense) => ({
  category: expense?.category || 'Other',
  description: expense?.description || '',
  amount: expense?.amount ?? '',
  vatAmount: expense?.vatAmount ?? '',
  expenseDate: expense?.expenseDate || toCalendarDateKey(new Date()),
  paymentMethod: expense?.paymentMethod || 'Cash',
  supplierId: expense?.supplierId || '',
  reference: expense?.reference || '',
  notes: expense?.notes || '',
  recurring: false,
  recurrence: {
    frequency: 'Monthly',
    intervalCount: 1,
    endMode: 'Never',
    occurrenceCount: '',
    endDate: '',
  },
  idempotencyKey: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`,
});

const ExpenseEditorDialog = ({
  open,
  expense,
  suppliers = [],
  allowRecurrence = true,
  saving = false,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(buildInitial);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(buildInitial(expense));
      setErrors({});
    }
  }, [open, expense]);

  const handleSubmit = () => {
    const nextErrors = validateExpenseForm({
      ...form,
      recurrence: {
        ...form.recurrence,
        startDate: form.expenseDate,
      },
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSave({
      ...form,
      supplierId: form.supplierId || null,
      recurring: allowRecurrence && form.recurring,
      recurrence: { ...form.recurrence, startDate: form.expenseDate },
    });
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      title={expense?.id ? t('expenses.dialog.editTitle') : t('expenses.dialog.createTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} disabled={saving} color="inherit">{t('common.cancel')}</Button>
          <PrimaryActionButton id="expense-save" onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box id="expense-editor-form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <SalovareSelectField
          id="expense-category"
          label={t('expenses.fields.category')}
          required
          value={form.category}
          error={Boolean(errors.category)}
          helperText={errors.category ? t(errors.category) : undefined}
          onChange={(event) => setForm((previous) => ({ ...previous, category: event.target.value }))}
        >
          {EXPENSE_CATEGORIES.map((category) => (
            <MenuItem key={category} value={category}>{t(`expenses.categories.${category}`)}</MenuItem>
          ))}
        </SalovareSelectField>
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>{t('expenses.fields.description')} *</Typography>
          <Box
            component="input"
            id="expense-description"
            aria-label={t('expenses.fields.description')}
            value={form.description}
            onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))}
            sx={nativeInputSx(Boolean(errors.description))}
          />
          {errors.description ? <Typography variant="caption" sx={{ color: 'error.main' }}>{t(errors.description)}</Typography> : null}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>{t('expenses.fields.amount')} *</Typography>
          <Box
            component="input"
            id="expense-amount"
            type="number"
            min={0}
            step="0.01"
            aria-label={t('expenses.fields.amount')}
            value={form.amount}
            onChange={(event) => setForm((previous) => ({ ...previous, amount: event.target.value }))}
            sx={nativeInputSx(Boolean(errors.amount))}
          />
          {errors.amount ? <Typography variant="caption" sx={{ color: 'error.main' }}>{t(errors.amount)}</Typography> : null}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>{t('expenses.fields.vatAmount')}</Typography>
          <Box
            component="input"
            id="expense-vat"
            type="number"
            min={0}
            step="0.01"
            aria-label={t('expenses.fields.vatAmount')}
            value={form.vatAmount}
            onChange={(event) => setForm((previous) => ({ ...previous, vatAmount: event.target.value }))}
            sx={nativeInputSx(Boolean(errors.vatAmount))}
          />
          {errors.vatAmount ? <Typography variant="caption" sx={{ color: 'error.main' }}>{t(errors.vatAmount)}</Typography> : null}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>{t('expenses.fields.date')} *</Typography>
          <SalovareDatePicker
            value={parseCalendarDateKey(form.expenseDate)}
            onChange={(date) => setForm((previous) => ({
              ...previous,
              expenseDate: date ? toCalendarDateKey(date) : '',
            }))}
            error={Boolean(errors.expenseDate)}
            helperText={errors.expenseDate ? t(errors.expenseDate) : undefined}
          />
        </Box>
        <SalovareSelectField
          id="expense-payment-method"
          label={t('expenses.fields.paymentMethod')}
          required
          value={form.paymentMethod}
          error={Boolean(errors.paymentMethod)}
          helperText={errors.paymentMethod ? t(errors.paymentMethod) : undefined}
          onChange={(event) => setForm((previous) => ({ ...previous, paymentMethod: event.target.value }))}
        >
          {IN_STORE_PAYMENT_METHODS.map((method) => (
            <MenuItem key={method} value={method}>{t(`storePaymentMethods.${getStorePaymentMethodKey(method)}`)}</MenuItem>
          ))}
        </SalovareSelectField>
        <SalovareSelectField
          id="expense-supplier"
          label={t('expenses.fields.supplier')}
          value={form.supplierId}
          displayEmpty
          onChange={(event) => setForm((previous) => ({ ...previous, supplierId: event.target.value }))}
        >
          <MenuItem value="">{t('expenses.fields.noSupplier')}</MenuItem>
          {suppliers.filter((item) => !item.isArchived).map((supplier) => (
            <MenuItem key={supplier.id} value={supplier.id}>{supplier.name}</MenuItem>
          ))}
        </SalovareSelectField>
        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>{t('expenses.fields.reference')}</Typography>
          <Box
            component="input"
            id="expense-reference"
            aria-label={t('expenses.fields.reference')}
            value={form.reference}
            onChange={(event) => setForm((previous) => ({ ...previous, reference: event.target.value }))}
            sx={nativeInputSx(false)}
          />
        </Box>
        {allowRecurrence && !expense?.id ? (
          <>
            <SwitchField
              id="expense-recurring"
              checked={form.recurring}
              onChange={(event) => setForm((previous) => ({ ...previous, recurring: event.target.checked }))}
              label={t('expenses.recurrence.enable')}
              helpText={t('expenses.recurrence.enableHelp')}
            />
            {form.recurring ? (
              <RecurrenceFields
                value={form.recurrence}
                startDate={form.expenseDate}
                errors={errors}
                onChange={(recurrence) => setForm((previous) => ({ ...previous, recurrence }))}
              />
            ) : null}
          </>
        ) : null}
      </Box>
    </ScrollableDialog>
  );
};

export default ExpenseEditorDialog;
